import { getAPIUrlV2 } from "../apiConfig";
import { liveUpdateRoute } from "../routes";
import { authenticatedFetchJSON } from "@/services/authenticatedFetch";

export default class LiveUpdateAPI {
    emitter = null;
    authService = null;
    socket = null;
    heartbeatInterval = null;
    reconnectTimeout = null;
    manuallyClosed = false;
    generation = 0;
    reconnectAttempts = 0;
    maxReconnectDelay = 30000; // Max 30 seconds between reconnects
    baseReconnectDelay = 1000; // Start with 1 second
    degradedWarningInterval = 60000;
    lastDegradedWarningAt = null;

    constructor(emitterInstance, authService = null) {
        this.emitter = emitterInstance;
        this.authService = authService;
    }

    async SubscribeToLiveUpdates() {
        this.Close();
        const generation = this.generation;
        this.manuallyClosed = false;
        try {
            const ws = await this.createWebSocket("subscriptions");
            if (this.manuallyClosed || generation !== this.generation) {
                ws.close();
                return;
            }
            this.socket = ws;
            this.setSocketListeners(ws);
        } catch (error) {
            if (this.manuallyClosed || generation !== this.generation) return;
            this.warnConnectionUnavailable(error);
            this.scheduleReconnect();
        }
    }

    clearHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    scheduleReconnect() {
        if (this.manuallyClosed) return;

        clearTimeout(this.reconnectTimeout);

        // Exponential backoff capped at maxReconnectDelay: 1s, 2s, 4s, 8s, 16s, 30s
        const cappedDelay = Math.min(
            this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelay
        );
        this.reconnectAttempts++;

        // Jitter (random 50-100% of the backoff) so that when the backend
        // restarts, clients don't reconnect in lockstep and stampede it.
        const delay = cappedDelay * (0.5 + Math.random() * 0.5);

        this.reconnectTimeout = setTimeout(() => {
            void this.SubscribeToLiveUpdates();
        }, delay);
    }

    warnConnectionUnavailable(error) {
        const now = Date.now();
        if (
            this.lastDegradedWarningAt !== null &&
            now - this.lastDegradedWarningAt < this.degradedWarningInterval
        ) {
            return;
        }

        this.lastDegradedWarningAt = now;
        const reason = error instanceof Error ? ` ${error.message}` : "";
        console.warn(
            `[WebSocket] Live updates unavailable. Reconnecting in the background.${reason}`
        );
    }

    startHeartbeat(ws) {
        // Clear any existing heartbeat interval
        this.clearHeartbeat();

        this.heartbeatInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
                try {
                    ws.send("heartbeat");
                } catch (error) {
                    console.error("Failed to send heartbeat:", error);
                    clearInterval(this.heartbeatInterval);
                }
            }
        }, 10000); // 10 seconds
    }

    setSocketListeners(ws) {
        ws.onopen = () => {
            if (this.socket !== ws || this.manuallyClosed) return;
            console.log("[WebSocket] Connection opened");
            // Reset reconnect attempts on successful connection
            this.reconnectAttempts = 0;
            // Start heartbeat to keep connection alive
            this.startHeartbeat(ws);
        };

        ws.onmessage = (event) => {
            if (this.socket !== ws || this.manuallyClosed) return;
            console.log("[WebSocket] Received:", event.data);
            this.emitter.emit(`live-update:${event.data}`);
        };

        // The close event carries the actionable reconnect state. Logging the
        // browser's raw error Event here produced one unactionable console
        // error per retry and obscured other failures.
        ws.onerror = () => {};

        ws.onclose = (event) => {
            if (this.socket !== ws || this.manuallyClosed) return;

            const closeReason = event?.reason
                ? new Error(event.reason)
                : new Error("Connection closed");
            this.warnConnectionUnavailable(closeReason);
            if (this.socket === ws) {
                this.socket = null;
            }
            this.clearHeartbeat();
            this.scheduleReconnect();
        };
    }

    async createWebSocket(endpoint) {
        const ticket = await this.getTicket();

        const wsURL = getAPIUrlV2(liveUpdateRoute).replace(/^http/, "ws");
        return new WebSocket(`${wsURL}/${endpoint}?ticket=${ticket}`);
    }

    // Fetch a short-lived, tenant-scoped ticket. This call is authenticated (JWT
    // + Customer-ID), so the backend binds the ticket to our own tenant; the
    // WebSocket then authenticates with it instead of a shared static token.
    async getTicket() {
        const data = await authenticatedFetchJSON(
            `${getAPIUrlV2(liveUpdateRoute)}/authenticate`,
            {
                method: "POST"
            },
            this.authService
        );

        if (!data.status) {
            throw new Error(`Authentication failed: ${data.error}`);
        }

        return data.data; // short-lived WebSocket auth ticket
    }

    Close() {
        this.generation++;
        this.manuallyClosed = true;
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;

        if (this.heartbeatInterval) {
            this.clearHeartbeat();
        }

        if (this.socket) {
            this.socket.onclose = null;
            this.socket.close();
            this.socket = null;
        }
    }
}
